import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import api from "./api";

Vue.use(Vuex);

const EVENT_TOKEN = "EventToken";
const EVENT_TOKEN_BE = "EventTokenBE";
const UPDATE_USER_DATA = "UserData";
const UPDATE_USER_DATA_BE = "UserDataBE";
const UPDATE_EVENT_DATA = "EventData"

const store = new Vuex.Store({
    state: {
        version: process.env.VERSION,
        shopData: null,
        cartItems: null,
        token: localStorage.getItem(EVENT_TOKEN) || null,
        tokenBE: localStorage.getItem(EVENT_TOKEN_BE) || null,
        user: null,
        userState: null,
        userData: JSON.parse(
            localStorage.getItem(UPDATE_USER_DATA)
                ? atob(localStorage.getItem(UPDATE_USER_DATA))
                : null
        ),
        userDataBE: JSON.parse(
            localStorage.getItem(UPDATE_USER_DATA_BE)
                ? atob(localStorage.getItem(UPDATE_USER_DATA_BE))
                : null
        ),
        eventData: JSON.parse(
            localStorage.getItem(UPDATE_EVENT_DATA)
                ? atob(localStorage.getItem(UPDATE_EVENT_DATA))
                : null
        )
    },

    mutations: {
        /* called using $store.commit() */
        UPDATE_SHOP_DATA(state, data) {
            state.shopData = data;
        },

        UPDATE_TOKEN(state, data) {
            state.token = data;
            if (state.token) {
                localStorage.setItem(EVENT_TOKEN, state.token);
            } else {
                localStorage.removeItem(EVENT_TOKEN);
            }
        },
        UPDATE_USER(state, data) {
            state.user = data;
        },
        UPDATE_USER_STATE(state, data) {
            state.userState = data;
        },
        UPDATE_TOKEN_BE(state, data) {
            state.tokenBE = data;
            if (state.tokenBE) {
                localStorage.setItem(EVENT_TOKEN_BE, state.tokenBE);
            } else {
                localStorage.removeItem(EVENT_TOKEN_BE);
            }
        },
        UPDATE_USER_DATA(state, data) {
            if (data && data.email) {
                var now = new Date();
                now.setMinutes(now.getMinutes() + 30); // 30 min expiration
                state.userData = {
                    email: data.email,
                    expire: now.getTime(),
                    firstName: data.firstName ? data.firstName : "",
                    lastName: data.lastName ? data.lastName : ""
                };
                localStorage.setItem(
                    UPDATE_USER_DATA,
                    btoa(JSON.stringify(state.userData))
                );
            } else {
                localStorage.removeItem(UPDATE_USER_DATA);
            }
        },
        UPDATE_USER_DATA_BE(state, data) {
            if (data) {
                state.userDataBE = data;
                localStorage.setItem(UPDATE_USER_DATA_BE, btoa(JSON.stringify(state.userDataBE)));
            } else {
                localStorage.removeItem(UPDATE_USER_DATA_BE);
            }
        },
        UPDATE_EVENT_DATA(state, data) {
            if (data) {
                state.eventData = data;
                localStorage.setItem(UPDATE_EVENT_DATA, btoa(JSON.stringify(state.eventData)));
            } else {
                localStorage.removeItem(UPDATE_EVENT_DATA);
            }
        }
    },

    actions: {
        /* called using $store.dispatch() */
        ADD_ITEM_TO_CART({ commit }, data) {
            return api
                .addItemToCart(data.photoId, data.articleCode, data.quantity, data.projectId)
                .then(result => {
                    commit("UPDATE_USER_STATE", result.userState);
                    return result;
                });
        },

        ADD_ALL_PHOTOS_TO_CART({ commit }, data) {
            return api
                .addAllPhotosToCart(
                    data.articleCode,
                    data.quantity,
                    data.onlyFavorites
                )
                .then(result => {
                    commit("UPDATE_USER_STATE", result.userState);
                    return result;
                });
        },

        GET_CART_ITEMS({ commit }) {
            return api.getCartItems().then(result => {
                commit("UPDATE_USER_STATE", result.userState);
                return result;
            });
        },

        EVENT_AUTH_CLEANUP({ commit }) {
            commit("UPDATE_TOKEN", null);
            commit("UPDATE_USER", null);
            commit("UPDATE_USER_DATA", null);
            commit("UPDATE_USER_STATE", null);
        },

        EVENT_BACKEND_AUTH_CLEANUP({ commit }) {
            commit("UPDATE_TOKEN_BE", null);
            commit("UPDATE_USER_DATA_BE", null);
        },

        GET_CHECKOUT_SUMMARY({ commit }) {
            return api.getCheckoutSummary().then(result => {
                commit("UPDATE_USER_STATE", result.userState);
                return result;
            });
        },

        GET_SHOP_DATA({ commit }) {
            return new Promise((resolve) => {
                api.getShopData().then(result => {
                    if (result.isGlobalSite && result.redirect) {
                        window.location.replace(result.redirect);
                        return;
                    }

                    commit("UPDATE_SHOP_DATA", result);

                    resolve(result);
                });
            });
        },

        GET_USER_DATA_BE({commit}) {
            return api.backendGetUserState().then(result => {
                commit("UPDATE_USER_DATA_BE", result.user);
                return result;
            });
        },

        // eslint-disable-next-line
        GET_EVENT_DATA({ commit }, data) {
            return api.getEventData(data.eventUrl).then(result => {
                commit("UPDATE_EVENT_DATA", result)
                return result;
            });
        },

        REMOVE_ITEM_FROM_CART({ commit }, data) {
            return api
                .removeItemFromCart(data.photoId, data.articleCode)
                .then(result => {
                    commit("UPDATE_USER_STATE", result.userState);
                    return result;
                });
        },

        VALIDATE_MAIL({ commit }, data) {
            return api
                .validateEmail(data.eventId, data.email, data.acceptTerms)
                .then(result => {
                    commit("UPDATE_TOKEN", result.token);
                    commit("UPDATE_USER", result.user);
                    commit("UPDATE_USER_STATE", result.userState);
                    return result;
                });
        },

        GET_EVENT_DATA_DASHBOARD({ commit }) {
            return api.getEventDataDashboard().then(result => {
                commit("UPDATE_USER_STATE", result.userState);
                return result;
            });
        },

        GET_GALLERY_DATA({ commit }, data) {
            return api.getGalleryData(data.albumId).then(result => {
                commit("UPDATE_USER_STATE", result.userState);
                return result;
            });
        },

        GET_ACCOUNT_PROFILE({ commit }) {
            return api.getAccountProfile().then(result => {
                commit("UPDATE_USER_STATE", result.userState);
                return result;
            });
        },

        BACKEND_LOGIN({ commit }, data) {
            return api
                .backendLogin(data.username, data.password, data.rememberMe)
                .then(result => {
                    commit("UPDATE_TOKEN_BE", result.token);
                    commit("UPDATE_USER_DATA_BE", result.user);
                    return result;
                });
        },

        ACCEPT_GDPR({commit}, data) {
            return api
                .backendAcceptGdpr(data.shopData)
                .then(result => {
                    commit("UPDATE_USER_DATA_BE", result.user);
                    return result;
                });
        },

        BACKEND_SET_FRONTEND_TOKEN({ commit }, data) {
            return this.dispatch("EVENT_AUTH_CLEANUP")
                .then(() => {
                    return api.backendGetFrontendToken(data.eventId);
                })
                .then(result => {
                    commit("UPDATE_TOKEN", result.token);
                    return result;
                });
        },

        UPDATE_USER_DATA({ commit }, data) {
            commit("UPDATE_USER_DATA", data);
        },

        UPDATE_USER_LOGIN({ commit }, data) {
            commit("UPDATE_TOKEN", data.token)
            commit("UPDATE_USER", data.user)
            commit("UPDATE_USER_DATA", data.user)
            commit("UPDATE_USER_STATE", data.userState)
        }
    },

    getters: {
        token(state) {
            return state.token;
        },

        tokenBE(state) {
            return state.tokenBE;
        },

        isAuthenticated(state) {
            return state.token !== null;
        },

        isAuthenticatedBE(state) {
            return state.tokenBE !== null;
        },

        shopData(state) {
            return state.shopData;
        },

        user(state) {
            return state.user;
        },

        eventData(state) {
            return state.eventData;
        },

        userState(state) {
            return state.userState;
        },

        userData(state) {
            // check expiration
            var now = new Date();
            if (state.userData) {
                var expired = true;

                try {
                    expired =
                        now.getTime() >=
                        new Date(state.userData.expire).getTime();
                } catch (error) {
                    console.error(error);
                }

                if (expired) {
                    return null;
                }
            }
            return state.userData;
        },

        userDataBE(state){
            return state.userDataBE;
        },

        hasAcceptedGDPR(state) {
            return state.userDataBE.flagGDPR;
        },

        isUser(state) {
            return (
                state.userState && state.userState.userType && state.userState.userType === "user"
            );
        },

        isVisitor(state) {
            return (
                state.userState && state.userState.userType && state.userState.userType === "visitor"
            );
        },

        isAuthor(state) {
            return (
                state.userState && state.userState.userType && state.userState.userType === "author"
            );
        },

        isAgent(state) {
            if (state.userDataBE && Object.prototype.hasOwnProperty.call(state.userDataBE, 'type')) {
                return state.userDataBE.type === 'A';
            } else {
                return false;
            }
        },

        isUserDataValid(state) {
            // check expiration -- FIXME: unificare il controllo con userData
            var valid = false;
            if (state.userData) {
                try {
                    valid =
                        new Date().getTime() <
                        new Date(state.userData.expire).getTime();
                } catch (error) {
                    console.error(error);
                }
            }
            return valid;
        },

        isPrivateEvent(state) {
            return (
                state.eventData && state.eventData.eventVisibility && state.eventData.eventVisibility === "PRIVATO"
            );
        }
    }
});

// Add a request interceptor
axios.interceptors.request.use(function(config) {
    var token = null;
    var tokenHeaderName = null;

    switch (config.area) {
        case "backend":
            token = store.getters.tokenBE;
            tokenHeaderName = "Event-Token-BE";
            break;
        case "frontend":
        default:
            token = store.getters.token;
            tokenHeaderName = "Event-Token";
            break;
    }
    if (token !== null) {
        config.headers[tokenHeaderName] = token;
    }

    config.headers["Event-Area"] = config.area;
    config.headers["Event-Url"] = window.location.href;

    return config;
});

export default store;
